// src/themes.js
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import EvelethCleanRegularWoff2 from './assets/fonts/EvelethCleanRegular.woff2';
import EvelethCleanRegularWoff from './assets/fonts/EvelethCleanRegular.woff';

const evelethCleanRegular = {
  fontFamily: 'EvelethCleanRegular',
  fontStyle: 'normal',
  fontWeight: 400,
  src: `
    local('EvelethCleanRegular'),
    url(${EvelethCleanRegularWoff2}) format('woff2'),
    url(${EvelethCleanRegularWoff}) format('woff')
  `,
  fontDisplay: 'swap',
};

// 🧱 Shared base theme
const baseTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#bf0020' },
    secondary: { main: '#961900' },
    background: {
      default: '#1c1c1c',
      paper: '#2b2b2b',
    },
    text: {
      primary: '#ffffff',
      secondary: '#cccccc',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
    fontSize: 14,
    h1: { fontFamily: 'EvelethCleanRegular', fontSize: '2rem', fontSize: 'clamp(1.75rem, 6vw, 2.5rem)' },
    h2: { fontFamily: 'Helvetica', fontSize: '2rem' },
    h3: { fontFamily: 'EvelethCleanRegular', fontSize: '1.5rem' },
    h4: { fontFamily: 'EvelethCleanRegular', fontSize: 'clamp(1rem, 3vw, 1.25rem)' },
    h5: { fontFamily: 'EvelethCleanRegular', fontSize: '.75rem' },
    h6: { fontFamily: 'EvelethCleanRegular', fontSize: '.9rem' },
    body1: {
      fontFamily: 'Helvetica',
      fontSize: '1rem',
      color: '#ededed',
    },
    button: {
      fontFamily: 'EvelethCleanRegular',
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
      fontWeight: 'bold',
    },
    subtitle1: {
      fontFamily: 'EvelethCleanRegular',
      fontSize: '.25rem',
    },
    input: {
      fontFamily: 'EvelethCleanRegular',
      fontSize: '.5rem',
    },
  },
  shadows: Array(25).fill('none'),  // <— Add this here
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [evelethCleanRegular],
        body: {
          backgroundColor: '#2b2b2b',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#bf0020',
          color: '#fff',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: '#555 !important',  // forces darker gray
        },
        root: {
          '& .Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#961900',  // your secondary color
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#2b2b2b',
          color: '#fff',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#2b2b2b',
          color: '#ffffff',
          boxShadow: 'none',  // removes elevation shadow overlay
          backgroundImage: 'none',  // disables MUI’s gradient overlay
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#2b2b2b',
          color: '#ffffff',
          fontWeight: 'bold',
          fontFamily: 'EvelethCleanRegular',
          margin: '5px',
        },
        deleteIcon: {
          color: '#ffffff',
          '&:hover': {
            color: '#ff0000',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontFamily: 'EvelethCleanRegular',
          fontSize: '0.85rem',
          backgroundColor: '#3a3a3a',
          color: '#ffffff',
          border: '1px solid #444',
          '&.Mui-selected': {
            backgroundColor: '#ffffff',
            color: '#3a3a3a',
            fontWeight: 'bold',
          },
          '&:hover': {
            backgroundColor: '#666666',
            color: '#fff',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          textDecoration: 'underline',
          fontFamily: 'EvelethCleanRegular',
          fontWeight: 700,
          textShadow: '3px 3px 2px #511B24',
          '&:hover': {
            color: '#BF0020',
            textDecoration: 'underline',
            fontWeight: 900,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '& a': {
            color: '#ffffff',
            textDecoration: 'underline',
            fontFamily: 'EvelethCleanRegular',
            fontWeight: 400,
            textShadow: '3px 3px 2px #511B24',
            '&:hover': {
              color: '#BF0020',
              textDecoration: 'underline',
              fontWeight: 700,
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontFamily: 'EvelethCleanRegular',
          fontWeight: 400,
        },
        primary: {
          fontWeight: 700,
          fontSize: '1rem',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontFamily: 'Helvetica',
          fontWeight: 'bold',
          fontSize: '1rem',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'EvelethCleanRegular',
          fontSize: '.85rem',
          fontWeight: 'bold',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontFamily: 'Helvetica',
            fontWeight: 'bold',
            fontSize: '1rem',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: 'EvelethCleanRegular',
          fontSize: '1rem',
        },
      },
    },
    MuiCollapse: {
      defaultProps: {
        timeout: 250,
      },
    },
  },
});

// 🌑 Dark theme = base
export const darkTheme = baseTheme;

// 🌞 Light theme = base + overrides
export const lightTheme = deepmerge(baseTheme, {
  palette: {
    mode: 'light',
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#444444',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#fafafa',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#bf0020',
          color: '#fff',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#000000',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#bf0020',
          color: '#fff',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#000000',
          '& a': {
            color: '#000000',
            textShadow: 'none',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#eeeeee',
          color: '#000000',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#000000',
          textShadow: 'none',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: '#000000',
        },
      },
    },
  },
});
